import {
  ArrowDownRight,
  ArrowUpRight,
  CalendarDays,
  Handshake,
  RefreshCw,
  Route,
  Truck,
  TriangleAlert,
  Users,
  Wrench,
} from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { buildApiUrl, cn, formatCurrency } from "../../lib/utils";
import { DashboardStats, MaintenanceAlert } from "./@types";
import { DashCard, EmptyState, Skeleton } from "./components/DashCard";
import DriverPerformanceCard from "./components/DriverPerformanceCard";
import FuelConsumptionChart from "./components/FuelConsumptionChart";
import KpiCard from "./components/KpiCard";
import RecentDriversTable from "./components/RecentDriversTable";
import TripOverviewChart from "./components/TripOverviewChart";
import { useGetCompanyDashboardStatsMutation } from "./dashboardApiSlice";

/* ================= Helpers ================= */

const MONTHS = Array.from({ length: 12 }, (_, i) =>
  new Date(2000, i, 1).toLocaleString(undefined, { month: "long" })
);

const YEARS_BACK = 5;

const severityStyles = (severity: string) => {
  switch (severity?.toLowerCase()) {
    case "critical":
    case "high":
    case "severe":
      return { badge: "bg-red-50 text-red-700", dot: "bg-red-500" };
    case "fair":
    case "medium":
    case "moderate":
      return { badge: "bg-amber-50 text-amber-700", dot: "bg-amber-500" };
    case "good":
    case "low":
    case "minor":
      return { badge: "bg-emerald-50 text-emerald-700", dot: "bg-emerald-500" };
    default:
      return { badge: "bg-slate-100 text-slate-600", dot: "bg-slate-400" };
  }
};

/** The API may omit or null out collections; normalize once so the UI never has to. */
const normalize = (raw: DashboardStats): DashboardStats => ({
  ...raw,
  maintenanceAlerts: raw.maintenanceAlerts ?? [],
  recentDrivers: raw.recentDrivers ?? [],
  recentAccidents: raw.recentAccidents ?? [],
  ongoingTrips: raw.ongoingTrips ?? [],
  tripsByMonth: raw.tripsByMonth ?? [],
  tripsByYear: raw.tripsByYear ?? [],
  fuelLogByVehicleTypes: raw.fuelLogByVehicleTypes ?? [],
  driverRanking: raw.driverRanking ?? [],
  bestPerformingDriver: raw.bestPerformingDriver ?? null,
  worstPerformingDriver: raw.worstPerformingDriver ?? null,
});

const percent = (part: number, whole: number) =>
  whole > 0 ? Math.round((part / whole) * 100) : 0;

/* ================= Sub-components ================= */

const DashboardSkeleton = () => (
  <div className="min-h-full bg-slate-50 p-4 sm:p-6">
    <div className="mx-auto w-full max-w-[1600px] space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-10 w-72" />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-[104px] rounded-2xl" />
        ))}
      </div>
      {[0, 1, 2].map((row) => (
        <div key={row} className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <Skeleton className="h-80 rounded-2xl xl:col-span-2" />
          <Skeleton className="h-80 rounded-2xl" />
        </div>
      ))}
      <Skeleton className="h-96 rounded-2xl" />
    </div>
  </div>
);

interface PeriodFilterProps {
  year: number;
  month: number;
  onChange: (next: { year: number; month: number }) => void;
  disabled?: boolean;
}

const PeriodFilter: React.FC<PeriodFilterProps> = ({
  year,
  month,
  onChange,
  disabled,
}) => {
  const now = new Date();
  const thisYear = now.getFullYear();
  const years = Array.from({ length: YEARS_BACK }, (_, i) => thisYear - i);
  // Don't offer months that haven't happened yet in the current year.
  const lastMonth = year === thisYear ? now.getMonth() + 1 : 12;

  const selectClass =
    "appearance-none bg-transparent py-2 pl-1 pr-2 text-sm font-medium text-slate-700 outline-none disabled:opacity-60";

  return (
    <div className="flex items-center rounded-xl border border-slate-200 bg-white px-3 shadow-sm focus-within:border-primary/50">
      <CalendarDays size={15} className="mr-1 shrink-0 text-slate-400" />
      <select
        aria-label="Month"
        value={month}
        disabled={disabled}
        onChange={(e) => onChange({ year, month: Number(e.target.value) })}
        className={selectClass}
      >
        {MONTHS.slice(0, lastMonth).map((name, i) => (
          <option key={name} value={i + 1}>
            {name}
          </option>
        ))}
      </select>
      <span className="h-4 w-px bg-slate-200" />
      <select
        aria-label="Year"
        value={year}
        disabled={disabled}
        onChange={(e) => {
          const nextYear = Number(e.target.value);
          const maxMonth = nextYear === thisYear ? now.getMonth() + 1 : 12;
          onChange({ year: nextYear, month: Math.min(month, maxMonth) });
        }}
        className={cn(selectClass, "pl-2")}
      >
        {years.map((y) => (
          <option key={y} value={y}>
            {y}
          </option>
        ))}
      </select>
    </div>
  );
};

interface FinancialSummaryProps {
  earnings: number;
  income: number;
  expenses: number;
  className?: string;
}

const FinancialSummary: React.FC<FinancialSummaryProps> = ({
  earnings,
  income,
  expenses,
  className,
}) => {
  const cashTotal = income + expenses;
  const incomeShare = cashTotal > 0 ? (income / cashTotal) * 100 : 0;
  const expenseShare = cashTotal > 0 ? 100 - incomeShare : 0;
  const net = income - expenses;
  const expenseRatio = percent(expenses, income);

  const tiles = [
    {
      label: "Total income",
      value: income,
      share: incomeShare,
      swatch: "bg-primary",
      icon: <ArrowUpRight size={15} />,
      iconClass: "bg-emerald-50 text-emerald-600",
    },
    {
      label: "Total expenses",
      value: expenses,
      share: expenseShare,
      swatch: "bg-red-400",
      icon: <ArrowDownRight size={15} />,
      iconClass: "bg-red-50 text-red-500",
    },
  ];

  return (
    <DashCard
      title="Total earnings"
      subtitle="Income against expenses for the period"
      className={className}
      action={
        <span
          className={cn(
            "shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold",
            net >= 0
              ? "bg-emerald-50 text-emerald-700"
              : "bg-red-50 text-red-700"
          )}
        >
          Net {net < 0 && "−"}
          {formatCurrency(Math.abs(net))}
        </span>
      }
    >
      <p className="break-words text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
        {formatCurrency(earnings)}
      </p>
      <p className="mt-1 text-xs text-slate-500">
        {income > 0
          ? `Expenses are ${expenseRatio}% of income`
          : "No income recorded for this period"}
      </p>

      <div className="mt-5 flex h-2.5 w-full gap-1 overflow-hidden rounded-full bg-slate-100">
        <div
          className="h-full rounded-full bg-primary transition-all duration-500"
          style={{ width: `${incomeShare}%` }}
        />
        <div
          className="h-full rounded-full bg-red-400 transition-all duration-500"
          style={{ width: `${expenseShare}%` }}
        />
      </div>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {tiles.map((tile) => (
          <div key={tile.label} className="rounded-xl border border-slate-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className={cn("h-2.5 w-2.5 rounded-sm", tile.swatch)} />
                <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                  {tile.label}
                </p>
              </div>
              <span
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-full",
                  tile.iconClass
                )}
              >
                {tile.icon}
              </span>
            </div>
            <p className="mt-4 break-words text-2xl font-semibold text-slate-900">
              {formatCurrency(tile.value)}
            </p>
            <p className="mt-1 text-xs text-slate-400">
              {Math.round(tile.share)}% of cash flow
            </p>
          </div>
        ))}
      </div>
    </DashCard>
  );
};

const AlertThumbnails: React.FC<{ alert: MaintenanceAlert }> = ({ alert }) => {
  const images = [alert.firstImageUrl, alert.lastImageUrl]
    .filter(Boolean)
    .filter((url, i, all) => all.indexOf(url) === i);

  if (images.length === 0) {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
        <Wrench size={18} />
      </div>
    );
  }

  return (
    <div className="flex shrink-0 -space-x-3">
      {images.map((url) => (
        <img
          key={url}
          src={buildApiUrl(url)}
          alt={alert.vehicleModelAndYear}
          className="h-10 w-10 rounded-lg border-2 border-white bg-slate-100 object-cover"
        />
      ))}
    </div>
  );
};

/* ================= Page ================= */

const CompanyDashboard: React.FC = () => {
  const { user } = useAuth();
  const [period, setPeriod] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  });

  const [getDashboardStats, { data: rawResponse, isLoading, isError }] =
    useGetCompanyDashboardStatsMutation();

  const fetchDashboardStats = useCallback(async () => {
    try {
      await getDashboardStats(period).unwrap();
    } catch (err) {
      console.error("Failed to fetch dashboard stats:", err);
    }
  }, [getDashboardStats, period]);

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  const response = useMemo(
    () => (rawResponse ? normalize(rawResponse) : undefined),
    [rawResponse]
  );

  if (isError && !response) {
    return (
      <div className="min-h-full bg-slate-50 p-4 sm:p-6">
        <DashCard className="mx-auto max-w-md">
          <EmptyState
            icon={<TriangleAlert size={20} />}
            message="We couldn't load your dashboard data."
          />
          <div className="flex justify-center">
            <button
              type="button"
              onClick={fetchDashboardStats}
              className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
            >
              Try again
            </button>
          </div>
        </DashCard>
      </div>
    );
  }

  if (!response) {
    return <DashboardSkeleton />;
  }

  const firstName = user?.fullName?.split(" ")[0];
  const monthLabel = MONTHS[period.month - 1].slice(0, 3);
  const completionRate = percent(
    response.totalCompletedTrips,
    response.totalTrips
  );

  return (
    <div className="min-h-full bg-slate-50 p-4 sm:p-6">
      <div className="mx-auto w-full max-w-[1600px] space-y-6">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">
              {firstName ? `Welcome back, ${firstName}` : "Welcome back"}
            </h1>
            <p className="text-sm text-slate-500">
              Here's what's happening across your fleet in{" "}
              {MONTHS[period.month - 1]} {period.year}.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <PeriodFilter
              year={period.year}
              month={period.month}
              onChange={setPeriod}
              disabled={isLoading}
            />
            <button
              type="button"
              onClick={fetchDashboardStats}
              disabled={isLoading}
              aria-label="Refresh dashboard"
              className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:opacity-60"
            >
              <RefreshCw
                size={15}
                className={isLoading ? "animate-spin" : ""}
              />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {isError && (
          <div className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2.5 text-sm text-amber-800">
            <TriangleAlert size={16} className="shrink-0" />
            Couldn't refresh — showing the last data we loaded.
          </div>
        )}

        <div
          className={cn(
            "space-y-6 transition-opacity",
            isLoading && "pointer-events-none opacity-60"
          )}
          aria-busy={isLoading}
        >
          {/* KPIs */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard
              icon={<Truck size={22} />}
              label="Total vehicles"
              value={response.totalVehicles}
              hint={`${response.maintenanceAlerts.length} with maintenance alerts`}
            />
            <KpiCard
              icon={<Users size={22} />}
              label="Total drivers"
              value={response.totalDrivers}
              hint={`${response.driverRanking.length} ranked this period`}
              tone="green"
            />
            <KpiCard
              icon={<Handshake size={22} />}
              label="Total partners"
              value={response.totalPartners}
              tone="violet"
            />
            <KpiCard
              icon={<Route size={22} />}
              label="Total trips"
              value={response.totalTrips}
              hint={`${response.totalOngoingTrips.toLocaleString()} ongoing · ${completionRate}% completed`}
              tone="amber"
            />
          </div>

          {/* Earnings + Ongoing trips */}
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <FinancialSummary
              className="xl:col-span-2"
              earnings={response.totalEarnings}
              income={response.totalIncome}
              expenses={response.totalExpenses}
            />

            <DashCard
              title="Ongoing trips"
              subtitle="Vehicles currently on the road"
              className="xl:max-h-[440px]"
              bodyClassName="max-h-[360px] overflow-y-auto pt-4"
              action={
                <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
                  {response.totalOngoingTrips} live
                </span>
              }
            >
              {response.ongoingTrips.length === 0 ? (
                <EmptyState
                  icon={<Route size={20} />}
                  message="No trips in progress"
                />
              ) : (
                <ul className="space-y-3">
                  {response.ongoingTrips.map((trip, idx) => (
                    <li
                      key={`${trip.vehicleModelAndYear}-${idx}`}
                      className="rounded-xl border border-slate-200 p-3.5 transition-colors hover:border-primary/40"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {trip.vehicleModelAndYear}
                        </p>
                        <span className="shrink-0 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                          {(trip.distance ?? 0).toLocaleString()} km
                        </span>
                      </div>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {trip.driverName}
                      </p>

                      <div className="mt-3 space-y-2 border-l-2 border-dashed border-slate-200 pl-3">
                        <p className="relative text-xs leading-snug text-slate-600">
                          <span className="absolute -left-[19px] top-1 h-2 w-2 rounded-full border-2 border-slate-400 bg-white" />
                          {trip.fromLocation}
                        </p>
                        <p className="relative text-xs leading-snug text-slate-600">
                          <span className="absolute -left-[19px] top-1 h-2 w-2 rounded-full bg-primary" />
                          {trip.toLocation}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </DashCard>
          </div>

          {/* Trip overview + Driver performance */}
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
            <TripOverviewChart
              className="xl:col-span-2"
              tripsByMonth={response.tripsByMonth}
              tripsByYear={response.tripsByYear}
              totalTrips={response.totalTrips}
              totalCompletedTrips={response.totalCompletedTrips}
              totalCancelledTrips={response.totalCancelledTrips}
              monthLabel={monthLabel}
            />

            <DriverPerformanceCard
              best={response.bestPerformingDriver}
              worst={response.worstPerformingDriver}
              ranking={response.driverRanking}
            />
          </div>

          {/* Fuel + Maintenance + Reports */}
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3">
            <FuelConsumptionChart
              className="lg:col-span-2 xl:col-span-1"
              fuelLog={response.fuelLogByVehicleTypes}
            />

            <DashCard
              title="Maintenance alerts"
              subtitle="Vehicles that need attention"
              bodyClassName="pt-4"
            >
              {response.maintenanceAlerts.length === 0 ? (
                <EmptyState
                  icon={<Wrench size={20} />}
                  message="No maintenance alerts"
                />
              ) : (
                <ul className="space-y-3">
                  {response.maintenanceAlerts.map((alert, idx) => {
                    const styles = severityStyles(alert.severityLevel);
                    return (
                      <li
                        key={`${alert.vehicleModelAndYear}-${idx}`}
                        className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <AlertThumbnails alert={alert} />
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900">
                              {alert.vehicleModelAndYear}
                            </p>
                            {alert.count > 0 && (
                              <p className="text-xs text-slate-500">
                                +{alert.count} more
                              </p>
                            )}
                          </div>
                        </div>
                        <span
                          className={cn(
                            "shrink-0 rounded-full px-2.5 py-1 text-xs font-medium capitalize",
                            styles.badge
                          )}
                        >
                          {alert.severityLevel}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </DashCard>

            <DashCard
              title="Accident reports"
              subtitle="Most recent accidents"
              bodyClassName="pt-4"
            >
              {response.recentAccidents.length === 0 ? (
                <EmptyState
                  icon={<TriangleAlert size={20} />}
                  message="No recent accidents"
                />
              ) : (
                <ul className="space-y-3">
                  {response.recentAccidents.map((accident, idx) => {
                    const styles = severityStyles(accident.severityLevel);
                    return (
                      <li
                        key={`${accident.vehicleModelAndYear}-${idx}`}
                        className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3"
                      >
                        <div className="flex min-w-0 items-center gap-3">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-50 text-red-500">
                            <TriangleAlert size={18} />
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900">
                              {accident.vehicleModelAndYear}
                            </p>
                            <p className="flex items-center gap-1.5 text-xs capitalize text-slate-500">
                              <span
                                className={cn(
                                  "h-1.5 w-1.5 rounded-full",
                                  styles.dot
                                )}
                              />
                              {accident.severityLevel}
                            </p>
                          </div>
                        </div>
                        <span className="shrink-0 text-xs text-slate-400">
                          {accident.duration}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </DashCard>
          </div>

          {/* Recent driver activity */}
          <RecentDriversTable drivers={response.recentDrivers} />
        </div>
      </div>
    </div>
  );
};

export default CompanyDashboard;
